import type {InsightSupplierRow} from "../../../hooks/useInsightSupplierListQuery";

/** Parent vs child within the supplier entity (prompt uses `parent_supplier:` / `supplier:`). */
export type SupplierPickerVariant = "parent" | "child";

/** One supplier tree pick; slot kind is always `supplier`. */
export type SupplierPickerSelection = {
    variant: SupplierPickerVariant;
    name: string;
};

export type SupplierTreeItem = {
    id: string;
    label: string;
    children?: SupplierTreeItem[];
    /** Parent supplier group name (`sp_name`). */
    parentName?: string;
    /** Child supplier name. */
    supplierName?: string;
    isGroup?: boolean;
};

function escapeGroupId(name: string): string {
    return encodeURIComponent(name).replace(/%/g, "_");
}

export function getSelectableName(item: SupplierTreeItem): string | undefined {
    if (item.parentName) {
        return item.parentName;
    }
    if (item.supplierName) {
        return item.supplierName;
    }
    return undefined;
}

export function getSupplierPickerSelection(
    item: SupplierTreeItem,
): SupplierPickerSelection | undefined {
    if (item.parentName) {
        return {variant: "parent", name: item.parentName};
    }
    if (item.supplierName) {
        return {variant: "child", name: item.supplierName};
    }
    return undefined;
}

/** Build parent/child tree nodes from paginated supplier-parent API rows (unique `sp_name` per row). */
export function buildSupplierTree(rows: InsightSupplierRow[]): SupplierTreeItem[] {
    if (!rows.length) {
        return [];
    }

    return rows.map((row) => {
        const groupName = row.sp_name.trim() || "Other";
        return {
            id: `parent_supplier-${escapeGroupId(groupName)}`,
            label: groupName,
            parentName: groupName,
            isGroup: true,
            children: row.suppliers.map((name) => ({
                id: escapeGroupId(name.trim()),
                label: name,
                supplierName: name,
            })),
        };
    });
}

export function collectExpandableIds(items: SupplierTreeItem[]): string[] {
    const ids: string[] = [];

    function walk(nodes: SupplierTreeItem[]) {
        for (const n of nodes) {
            if (n.children?.length) {
                ids.push(n.id);
                walk(n.children);
            }
        }
    }

    walk(items);
    return ids;
}

export function findTreeItemById(
    items: SupplierTreeItem[],
    itemId: string,
): SupplierTreeItem | undefined {
    for (const n of items) {
        if (n.id === itemId) {
            return n;
        }
        if (n.children?.length) {
            const found = findTreeItemById(n.children, itemId);
            if (found) {
                return found;
            }
        }
    }
    return undefined;
}

/** All selectable item ids currently visible in the tree (parents and leaves). */
export function collectVisibleSelectableItemIds(items: SupplierTreeItem[]): string[] {
    const ids: string[] = [];

    function walk(nodes: SupplierTreeItem[]) {
        for (const n of nodes) {
            if (getSelectableName(n)) {
                ids.push(n.id);
            }
            if (n.children?.length) {
                walk(n.children);
            }
        }
    }

    walk(items);
    return ids;
}

/** Resolve selected item ids to agent prompt segments (order preserved). */
export function collectSelectionsFromIds(
    itemIds: readonly string[],
    items: SupplierTreeItem[],
    nameById?: ReadonlyMap<string, string>,
): SupplierPickerSelection[] {
    const selections: SupplierPickerSelection[] = [];
    for (const id of itemIds) {
        const item = findTreeItemById(items, id);
        const selection = item
            ? getSupplierPickerSelection(item)
            : undefined;
        if (selection) {
            selections.push(selection);
            continue;
        }
        const fallbackName = nameById?.get(id);
        if (fallbackName) {
            selections.push({variant: "child", name: fallbackName});
        }
    }
    return selections;
}

/** Resolve selected item ids to display names (order preserved). */
export function collectSelectionNamesFromIds(
    itemIds: readonly string[],
    items: SupplierTreeItem[],
    nameById?: ReadonlyMap<string, string>,
): string[] {
    return collectSelectionsFromIds(itemIds, items, nameById).map((s) => s.name);
}

/** Map stored names to tree item ids (order follows `names`; first match per name). */
export function findItemIdsByNamesInOrder(
    items: SupplierTreeItem[],
    names: readonly string[],
): string[] {
    const ids: string[] = [];

    function findFirstIdByName(nodes: SupplierTreeItem[], name: string): string | undefined {
        for (const n of nodes) {
            if (getSelectableName(n) === name) {
                return n.id;
            }
            if (n.children?.length) {
                const found = findFirstIdByName(n.children, name);
                if (found) {
                    return found;
                }
            }
        }
        return undefined;
    }

    for (const name of names) {
        const id = findFirstIdByName(items, name);
        if (id && !ids.includes(id)) {
            ids.push(id);
        }
    }
    return ids;
}
